import { useEffect, useState } from "react";
import { Accordion } from "@/components/Accordion";
import { Alert } from "@/components/Alert";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { confirmFormNavigation } from "@/hooks/useFormProtection";
import { ProviderField } from "@/pages/Providers/ProviderField";
import { ProviderDuplicateDialog } from "@/pages/Providers/ProviderDuplicateDialog";
import { providerGroups } from "@/pages/Providers/fields";
import { displayDate, displayValue } from "@/pages/Providers/display";
import { useProviderEditor } from "@/pages/Providers/useProviderEditor";
import { useProviderContext } from "@/pages/Providers/useProviderApi";
import type {
    Catalogs,
    Provider,
    ProviderValues,
} from "@/pages/Providers/types";

interface ProviderEditorProps {
    provider?: Provider;
    catalogs: Catalogs;
    onClose: () => void;
    onSaved: (provider: Provider) => void;
    onView: (id: number) => void;
}

export const ProviderEditor = ({
    provider,
    catalogs,
    onClose,
    onSaved,
    onView,
}: ProviderEditorProps) => {
    const { t, language } = useProviderContext();
    const editor = useProviderEditor({ provider, catalogs, onSaved });
    const {
        form,
        matches,
        setMatches,
        observation,
        setObservation,
        busy,
        error,
        checkIdentity,
        submit,
        confirm,
        initial,
    } = editor;
    const { isDirty, dirtyFields } = form.formState;
    const values = form.watch();
    const changed = Object.keys(dirtyFields) as (keyof ProviderValues)[];
    const [expanded, setExpanded] = useState<string[]>(["general"]);
    const errors = form.formState.errors;
    useEffect(() => {
        const group = providerGroups.find((entry) =>
            entry.fields.some((field) => errors[field.name])
        );
        if (group) {
            setExpanded((previous) =>
                previous.includes(group.name)
                    ? previous
                    : [...previous, group.name]
            );
        }
    }, [errors]);

    const cancelOperation = (): void => {
        if (!busy) {
            setMatches([]);
            confirmFormNavigation(onClose);
        }
    };

    return (
        <>
            <Modal
                isOpen
                maxWidth="2xl"
                onClose={onClose}
                hasUnsavedChanges={isDirty}
                title={t(
                    provider ? "providers.ui.edit_title" : "providers.ui.new"
                )}
                onSubmit={() => {
                    void submit();
                }}
                isSubmitting={busy}
                submitLabel={t("providers.ui.save")}
                submittingLabel={t("providers.ui.saving")}
                cancelLabel={t("providers.ui.cancel")}
                headerActions={
                    isDirty ? (
                        <Badge variant="warning">
                            {t("providers.ui.unsaved")}
                        </Badge>
                    ) : undefined
                }
            >
                <form
                    onSubmit={submit}
                    noValidate
                    className="flex flex-col gap-4"
                >
                    <p className="text-xs text-slate-500 dark:text-zinc-400">
                        {t("providers.ui.required_hint")}
                    </p>
                    {provider && (
                        <dl className="flex flex-wrap gap-x-6 gap-y-2 rounded-lg bg-slate-50 p-3 text-xs dark:bg-white/5">
                            <div>
                                <dt className="text-slate-500 dark:text-zinc-400">
                                    {t("providers.fields.id")}
                                </dt>
                                <dd className="mt-1 font-medium">
                                    {provider.id}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-slate-500 dark:text-zinc-400">
                                    {t("providers.fields.registered_at")}
                                </dt>
                                <dd className="mt-1 font-medium">
                                    {displayDate(
                                        provider.registered_at,
                                        language
                                    )}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-slate-500 dark:text-zinc-400">
                                    {t("providers.fields.bc_code")}
                                </dt>
                                <dd className="mt-1 font-medium">
                                    {provider.bc_code ??
                                        t("providers.filters.bc_unlinked")}
                                </dd>
                            </div>
                        </dl>
                    )}
                    {provider?.bc_present && (
                        <Alert variant="info">
                            {t("providers.ui.bc_explanation")}
                        </Alert>
                    )}
                    {error && <Alert variant="error">{t(error)}</Alert>}
                    {providerGroups.map((group) => (
                        <Accordion
                            key={group.name}
                            title={t(`providers.sections.${group.name}`)}
                            expanded={expanded.includes(group.name)}
                            onChange={(open) =>
                                setExpanded((previous) =>
                                    open
                                        ? [...previous, group.name]
                                        : previous.filter(
                                              (name) => name !== group.name
                                          )
                                )
                            }
                        >
                            <div className="flex flex-col gap-4 border-t border-slate-100 p-4 dark:border-white/10 sm:p-5">
                                {group.name === "address" && (
                                    <p className="text-xs text-slate-500">
                                        {t("providers.ui.country_help")}
                                    </p>
                                )}
                                {group.name === "accounting" && (
                                    <Alert variant="info">
                                        {t("providers.ui.catalog_help")}
                                    </Alert>
                                )}
                                {group.fields.map((definition) => (
                                    <ProviderField
                                        key={definition.name}
                                        definition={definition}
                                        control={form.control}
                                        catalogs={catalogs}
                                        managed={
                                            catalogs.bc_managed_fields.includes(
                                                definition.name
                                            ) ||
                                            Boolean(
                                                definition.catalog &&
                                                    [
                                                        "payment_methods",
                                                        "validation_states",
                                                    ].includes(
                                                        definition.catalog
                                                    )
                                            )
                                        }
                                        locked={
                                            provider?.locked_fields.includes(
                                                definition.name
                                            ) ?? false
                                        }
                                        onIdentityBlur={checkIdentity}
                                    />
                                ))}
                            </div>
                        </Accordion>
                    ))}
                    {provider && changed.length > 0 && (
                        <section className="rounded-xl border border-slate-200 p-4">
                            <h3 className="mb-3 font-semibold">
                                {t("providers.ui.pending_changes")}
                            </h3>
                            <dl className="flex flex-col gap-3 text-sm">
                                {changed.map((field) => (
                                    <div key={field}>
                                        <dt className="font-medium">
                                            {t(`providers.fields.${field}`)}
                                        </dt>
                                        <dd>
                                            {displayValue(
                                                field,
                                                initial[field],
                                                catalogs,
                                                t
                                            )}{" "}
                                            →{" "}
                                            {displayValue(
                                                field,
                                                values[field],
                                                catalogs,
                                                t
                                            )}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </section>
                    )}
                </form>
            </Modal>
            {matches.length > 0 && (
                <ProviderDuplicateDialog
                    conflicts={matches}
                    observation={observation}
                    busy={busy}
                    onObservation={setObservation}
                    onBack={() => setMatches([])}
                    onCancel={cancelOperation}
                    onConfirm={confirm}
                    onView={onView}
                />
            )}
        </>
    );
};
